import { FC, memo } from "react";
import { useFormContext } from '../forms/form'
import FieldError, { useFieldValidity } from './fieldError'

interface ToggleInputProps {
    label: string
    checked: boolean | string
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    required?: boolean
    disabled?: boolean
    helperText?: string
    wide?: boolean
}

const ToggleInput: FC<ToggleInputProps> = ({ label, checked, onChange, required = false, disabled = false, helperText, wide = false }) => {
    const isChecked = typeof checked === 'string' ? checked === 'true' ? true : false : checked
    const { disableInputs, isLoading } = useFormContext()
    const { errorMessage, isInvalid, fieldClassName, onBlur, onChange: onValidityChange, onInvalid } = useFieldValidity()
    const isDisabled = disabled || disableInputs

    return (
        <div className={wide ? "w-fit min-w-0 max-w-full" : "input-holder"}>
            <label className={`p-0 flex w-fit max-w-full flex-row items-center gap-x-2 min-w-0 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <span className={`min-w-0 ${wide ? "" : "flex-1"}`}>{label}{required ? '*' : ''}</span>
                <span className={`relative inline-block h-6 w-11 shrink-0 ${isDisabled ? 'opacity-30' : ''} ${isLoading ? 'animate-pulse' : ''}`}>
                    <input
                        type="checkbox"
                        role="switch"
                        aria-checked={isChecked}
                        checked={isChecked}
                        onChange={(e) => {
                            onValidityChange(e)
                            onChange(e)
                        }}
                        onBlur={onBlur}
                        onInvalid={onInvalid}
                        required={required}
                        disabled={isDisabled}
                        className={`${fieldClassName} sr-only`}
                    />
                    <span
                        aria-hidden="true"
                        className={`pointer-events-none absolute inset-0 rounded-full transition-colors duration-200 ${isChecked ? 'bg-customSecondary' : 'bg-gray-300'} ${isInvalid ? 'ring-2 ring-[#FF4747]' : 'peer-focus-visible:ring-2 peer-focus-visible:ring-customSecondary peer-focus-visible:ring-offset-2'}`}
                    />
                    <span
                        aria-hidden="true"
                        className={`pointer-events-none absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${isChecked ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                </span>
            </label>
            <FieldError message={errorMessage} />
            {helperText && (
                <div className="text-xs text-gray-500 mt-1">
                    {helperText}
                </div>
            )}
        </div>
    )
}

export default memo(ToggleInput)
